import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

const EMOJIS = [
  "🚨", "🚔", "🛑", "⚠️", "🔥", "🧯", "🔒", "🔓", "🗝️", "📞",
  "📱", "💡", "🔌", "👮", "🕵️", "🚒", "🚓", "🚑", "🏁", "🎯",
  "🚪", "🔦", "📷", "🎥", "📦", "📄", "🧾", "✍️", "✅", "❌",
  "🟢", "🟡", "🔵", "🟣",
];

const initialTypeForm = {
  label: "",
  type_key: "",
  icon_kind: "emoji",
  icon_value: "⚠️",
  color: "#2980b9",
  is_active: true,
  sort_order: 50,
};

export default function AddIncidents() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [typeForm, setTypeForm] = useState(initialTypeForm);
  const [keyTouched, setKeyTouched] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const fetchPageData = async (local, token) => {
    try {
      const [dashboardRes, usersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/incidents/guards`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!usersRes.ok) throw new Error("Failed to fetch guards");

      const dashboardResult = await dashboardRes.json();
      const usersResult = await usersRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const allUsers = usersResult?.data || [];
      setUsers(Array.isArray(allUsers) ? allUsers : []);
    } catch (error) {
      console.error("Add incidents page error:", error);
      setErrorMessage(error.message || "Failed to load page data.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return users;

    return users.filter((user) => {
      const memberId = String(user.member_id || "").toLowerCase();
      const name = String(user.name || "").toLowerCase();
      const username = String(user.username || "").toLowerCase();

      return memberId.includes(keyword) || name.includes(keyword) || username.includes(keyword);
    });
  }, [users, search]);

  const handleSubmitType = async (e) => {
    e.preventDefault();

    if (!typeForm.label.trim() || !typeForm.type_key.trim() || !typeForm.icon_value.trim()) {
      setErrorMessage("Please fill Label, Type Key and Icon.");
      return;
    }

    if (!/^#[0-9a-fA-F]{6}$/.test(typeForm.color)) {
      setErrorMessage("Please select a valid hex color.");
      return;
    }

    try {
      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const payload = {
        label: typeForm.label.trim(),
        type_key: slugify(typeForm.type_key.trim()),
        icon_kind: typeForm.icon_kind,
        icon_value: typeForm.icon_value.trim(),
        color: typeForm.color,
        is_active: typeForm.is_active ? 1 : 0,
        sort_order: Number(typeForm.sort_order || 100),
      };

      const res = await fetch(`${API_BASE_URL}/incidents/types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to create incident type");
      }

      setSuccessMessage(`Incident type "${payload.label}" created successfully!`);
      setTypeForm(initialTypeForm);
      setKeyTouched(false);
      setShowModal(false);
    } catch (error) {
      console.error("Create incident type error:", error);
      setErrorMessage(error.message || "Failed to create incident type.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = (memberId) => {
    navigate(`/incident_per?mode=edit&member_id=${encodeURIComponent(memberId)}`);
  };

  const handleSentinelGuardList = () => {
    navigate("/sentinel_incident");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading incident types page...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="incidents"
      activeMenu="incidents"
    >
      <PageHeader
        onOpenAdd={() => setShowModal(true)}
        onOpenSentinel={handleSentinelGuardList}
      />

      {successMessage && <Alert type="success" message={successMessage} />}
      {errorMessage && <Alert type="error" message={errorMessage} />}

      <FormSection
        title="Guard Incident Assignment"
        subtitle="Search guards and manage their incident type mapping"
      >
        <div className="mb-5">
          <input
            type="text"
            placeholder="Search by member ID, name, or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            style={{ fontFamily: FONT_UI }}
          />
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full min-w-[850px] border-collapse" style={{ fontFamily: FONT_UI }}>
            <thead>
              <tr className="bg-slate-900 text-white">
                <Th>Member ID</Th>
                <Th>User</Th>
                <Th>Username</Th>
                <Th>Role</Th>
                <Th>Actions</Th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-500">
                    No users found in the database.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <tr key={`${user.member_id}-${index}`} className="bg-white hover:bg-slate-50">
                    <Td>
                      <span className="text-blue-600 font-bold">{user.member_id}</span>
                    </Td>

                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                          {String(user.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="font-semibold text-slate-900">{user.name}</div>
                      </div>
                    </Td>

                    <Td>{user.username}</Td>

                    <Td>
                      <span className="inline-flex px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
                        {user.role || "Guard"}
                      </span>
                    </Td>

                    <Td>
                      <button
                        type="button"
                        onClick={() => handleEditUser(user.member_id)}
                        className="px-4 py-2 rounded-xl bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600"
                        style={{ fontFamily: FONT_UI }}
                      >
                        Edit
                      </button>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </FormSection>

      {showModal && (
        <IncidentTypeModal
          form={typeForm}
          setForm={setTypeForm}
          setKeyTouched={setKeyTouched}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmitType}
          saving={saving}
        />
      )}
    </Layout>
  );
}

function slugify(value = "") {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function PageHeader({ onOpenAdd, onOpenSentinel }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Incident Types
        </h2>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Manage all incident types for guards
        </p>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3 className="m-0 text-[22px] font-normal text-slate-900" style={{ fontFamily: FONT_HEADING }}>
            User Incident Type Management
          </h3>
          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Add new incident types and assign them to guards
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onOpenSentinel}
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold shadow-lg shadow-slate-500/20 hover:bg-slate-800"
            style={{ fontFamily: FONT_UI }}
          >
            For Sentinel
          </button>

          <button
            type="button"
            onClick={onOpenAdd}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20"
            style={{
              background: skin.gradient,
              fontFamily: FONT_UI,
            }}
          >
            + Add New Type
          </button>
        </div>
      </div>
    </>
  );
}

function IncidentTypeModal({
  form,
  setForm,
  setKeyTouched,
  onClose,
  onSubmit,
  saving,
}) {
  const previewLabel = form.label || "New Type";
  const previewIcon = form.icon_value || "⚠️";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;

    setForm((prev) => {
      const updated = { ...prev, [name]: nextValue };

      if (name === "label" && !prev.type_key) {
        updated.type_key = slugify(value);
      }

      return updated;
    });
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/45 backdrop-blur-sm flex items-center justify-center p-4 z-[2500]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[760px] bg-white rounded-2xl overflow-hidden shadow-2xl border border-slate-100 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100">
          <h3 className="m-0 text-[22px] font-normal text-slate-900" style={{ fontFamily: FONT_HEADING }}>
            Add Incident Type
          </h3>

          <button
            type="button"
            onClick={onClose}
            className="border-0 bg-transparent text-3xl leading-none cursor-pointer text-slate-500 hover:text-slate-900"
          >
            ×
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div
                className="h-11 w-11 rounded-xl flex items-center justify-center text-xl"
                style={{ background: form.color }}
              >
                {previewIcon}
              </div>

              <div className="text-base font-bold text-slate-900" style={{ fontFamily: FONT_UI }}>
                {previewLabel}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Label *">
                <Input
                  name="label"
                  value={form.label}
                  onChange={handleChange}
                  placeholder="Theft"
                  required
                />
              </Field>

              <Field label="Type Key *">
                <Input
                  name="type_key"
                  value={form.type_key}
                  onChange={(e) => {
                    setKeyTouched(true);
                    handleChange(e);
                  }}
                  placeholder="theft"
                  required
                />
              </Field>

              <Field label="Icon Kind">
                <Select name="icon_kind" value={form.icon_kind} onChange={handleChange}>
                  <option value="emoji">Emoji</option>
                  <option value="icon">Custom code</option>
                </Select>
              </Field>

              <Field label="Icon Value *">
                <Input
                  name="icon_value"
                  value={form.icon_value}
                  onChange={handleChange}
                  placeholder={form.icon_kind === "emoji" ? "Pick or paste emoji" : "bx bx-bell"}
                  required
                />
              </Field>

              <Field label="Color">
                <input
                  type="color"
                  name="color"
                  value={form.color}
                  onChange={handleChange}
                  className="w-full h-[46px] p-1.5 rounded-xl border border-slate-200 outline-none bg-white"
                />
              </Field>

              <Field label="Sort Order">
                <Input
                  type="number"
                  name="sort_order"
                  value={form.sort_order}
                  onChange={handleChange}
                />
              </Field>
            </div>

            <div className="mt-4">
              <label
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900"
                style={{ fontFamily: FONT_UI }}
              >
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                Enabled
              </label>
            </div>

            <div className="mt-5">
              <div className="mb-2 text-sm font-semibold text-slate-900" style={{ fontFamily: FONT_UI }}>
                Emoji Picker
              </div>

              <div className="grid grid-cols-[repeat(auto-fill,minmax(42px,1fr))] gap-2 p-2.5 border border-slate-200 rounded-xl bg-slate-50 max-h-[180px] overflow-y-auto">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        icon_kind: "emoji",
                        icon_value: emoji,
                      }))
                    }
                    className={`rounded-xl px-1.5 py-2 text-xl border cursor-pointer ${
                      form.icon_value === emoji
                        ? "border-blue-600 bg-blue-50"
                        : "border-transparent bg-white hover:bg-slate-100"
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
              style={{ fontFamily: FONT_UI }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ fontFamily: FONT_UI }}
            >
              {saving ? "Saving..." : "Save Type"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function FormSection({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
      <div className="mb-5">
        <h3 className="m-0 text-[22px] font-normal text-slate-900" style={{ fontFamily: FONT_HEADING }}>
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {subtitle}
        </p>
      </div>

      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block mb-2 text-sm font-semibold text-slate-900" style={{ fontFamily: FONT_UI }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </select>
  );
}

function Th({ children }) {
  return (
    <th className="text-left px-4 py-3.5 text-sm font-semibold">
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td className="px-4 py-3.5 border-b border-slate-200 text-sm text-slate-700">
      {children}
    </td>
  );
}